/**
 * Regole del menu del Magazzino per il gruppo E-commerce (08 sez. 4, 15 sez. 3.1, I-06). Modulo puro.
 * - Voci con `visibleWhen: 'commerce'`: solo con almeno un negozio (hasCommerce) e settings_ecommerce.view.
 * - Voci con `visibleWhen: 'legacyWoo'`: solo alle org con WooCommerce sul sistema vecchio.
 * - Voci con `visibleWhen: 'wooUsers'`: WooCommerce vecchio oppure un negozio WooCommerce sul motore (utenti B2B del sito).
 * - Voci con `minPhase`: nascoste finche' la fase dell'interfaccia non le porta.
 * - Layout salvato (menu personalizzato): le voci e-commerce visibili mancanti si aggiungono da sole
 *   (in coda come gruppo proprio, o in fondo al gruppo dove l'utente ha messo le altre). Le voci
 *   WooCommerce vecchie tolte dall'utente non tornano.
 */
import { COMMERCE_UI_PHASE } from './phase';

export type MenuVisibility = 'commerce' | 'legacyWoo' | 'wooUsers';

export interface MenuItemMeta {
  visibleWhen?: MenuVisibility;
  minPhase?: number;
}

export interface MenuCtx {
  hasCommerce: boolean;
  hasLegacyWoo: boolean;
  /** Almeno un negozio WooCommerce sul motore (non scollegato). */
  hasWooStore?: boolean;
  canViewCommerce: boolean;
  /** Fase dell'interfaccia e-commerce (default COMMERCE_UI_PHASE). */
  phase?: number;
}

/**
 * Contesto usato quando chi disegna il menu non lo passa: come prima del motore (voci WooCommerce
 * vecchie visibili, voci nuove nascoste). Nessuna org vede voci nuove finche' il chiamante non sa.
 */
export const DEFAULT_MENU_CTX: MenuCtx = Object.freeze({ hasCommerce: false, hasLegacyWoo: true, canViewCommerce: false });

export function isMenuItemVisible(meta: MenuItemMeta | undefined, ctx: MenuCtx): boolean {
  if (!meta) return true;
  if (meta.minPhase !== undefined && meta.minPhase > (ctx.phase ?? COMMERCE_UI_PHASE)) return false;
  if (meta.visibleWhen === 'commerce') return ctx.hasCommerce && ctx.canViewCommerce;
  if (meta.visibleWhen === 'legacyWoo') return ctx.hasLegacyWoo;
  if (meta.visibleWhen === 'wooUsers') return ctx.hasLegacyWoo || ctx.hasWooStore === true;
  return true;
}

export interface LayoutGroup {
  groupKey: string;
  items: string[];
}

/**
 * Applica visibilita' e regola di coda. `saved` = true se `layout` e' un menu personalizzato salvato.
 * Restituisce gruppi nuovi (non modifica l'input); i gruppi rimasti vuoti li toglie chi disegna.
 */
export function applyMenuRules(
  layout: readonly LayoutGroup[],
  saved: boolean,
  meta: (id: string) => MenuItemMeta | undefined,
  commerceGroup: LayoutGroup,
  ctx: MenuCtx,
): LayoutGroup[] {
  const groups = layout.map((g) => ({ groupKey: g.groupKey, items: g.items.filter((id) => isMenuItemVisible(meta(id), ctx)) }));
  if (!saved) return groups;

  const isCommerceItem = (id: string) => meta(id)?.visibleWhen === 'commerce';
  const wanted = commerceGroup.items.filter((id) => isCommerceItem(id) && isMenuItemVisible(meta(id), ctx));
  if (wanted.length === 0) return groups;

  const present = new Set(groups.flatMap((g) => g.items));
  const missing = wanted.filter((id) => !present.has(id));
  if (missing.length === 0) return groups;

  // gruppo dove l'utente ha messo la prima voce e-commerce gia' presente
  const host = groups.find((g) => g.items.some(isCommerceItem))
    ?? groups.find((g) => g.groupKey === commerceGroup.groupKey);
  if (host) {
    host.items.push(...missing);
    return groups;
  }
  return [...groups, { groupKey: commerceGroup.groupKey, items: missing }];
}

/**
 * Contesto che usano la sidebar e l'editor del menu. Finche' l'org non ha un negozio del motore (e con
 * il motore spento non puo' averne), o finche' la presenza si sta leggendo, o senza il permesso di
 * vedere l'e-commerce, vale DEFAULT_MENU_CTX: menu identico a prima del motore (regola del rilascio:
 * nessun comportamento nuovo per le org esistenti). La scomparsa delle voci WooCommerce per chi non
 * ha WooCommerce (15 sez. 3.1 punto "Rischio") scatta solo insieme al primo negozio.
 * `menuEnabled`: l'azienda ha acceso la sezione E-commerce da Impostazioni > Voci menu prima di avere un
 * negozio (per arrivare a "Collega il tuo primo negozio"): vale come se il negozio ci fosse.
 */
export function sidebarMenuCtx(c: MenuCtx & { loading: boolean; menuEnabled?: boolean }): MenuCtx {
  if (c.loading || !(c.hasCommerce || c.menuEnabled === true) || !c.canViewCommerce) return DEFAULT_MENU_CTX;
  return { hasCommerce: true, hasLegacyWoo: c.hasLegacyWoo, hasWooStore: c.hasWooStore === true, canViewCommerce: true, phase: c.phase ?? COMMERCE_UI_PHASE };
}

export type BadgeKey = 'commerce_conflicts' | 'commerce_attention';
export type MenuBadge = { kind: 'count'; value: number } | { kind: 'dot'; tone: 'red' | 'amber' } | null;

/**
 * Segnale di una voce (08 sez. 4, 15 sez. 3.1 punto 3): Conflitti = numero ambra solo se > 0;
 * Panoramica = pallino rosso (bloccante) o ambra (freno, analisi pronta). Nessun numero per lavori o errori.
 */
export function menuBadge(key: BadgeKey | undefined, s: { conflicts: number; attention: 'none' | 'amber' | 'red' }): MenuBadge {
  if (key === 'commerce_conflicts') return s.conflicts > 0 ? { kind: 'count', value: s.conflicts } : null;
  if (key === 'commerce_attention') return s.attention === 'none' ? null : { kind: 'dot', tone: s.attention };
  return null;
}

/** Pallino da riportare sull'etichetta di un gruppo chiuso o sull'icona nel menu collassato: il piu' grave. */
export function strongestDot(badges: readonly MenuBadge[]): 'red' | 'amber' | null {
  let out: 'red' | 'amber' | null = null;
  for (const b of badges) {
    if (!b) continue;
    if (b.kind === 'dot' && b.tone === 'red') return 'red';
    out = 'amber';
  }
  return out;
}
